<script lang="ts">
export default {
  name: 'ReportingList',
};

interface Target {
  card: Card | null;
  action: 'edit' | 'delete' | 'publish' | 'unpublish' | null;
  type: 'tableau' | 'document' | null;
  modal: {
    title: string;
    open: boolean;
    actionLabel: string;
  };
}
</script>

<script setup lang="ts">
import {
  ExternalLinkIcon,
  Trash2Icon,
  XIcon,
  EditIcon,
  ChartNoAxesCombinedIcon,
  FileIcon,
  UsersIcon,
  PinIcon
} from 'lucide-vue-next';
import AppModal from '@/components/v2/AppModal.vue';
import TableauModal from '@/components/TableauModal.vue';
import { TableauDashboard, TableauType } from '@/types/TableauDashboard';
import { ReportingTableauType } from '@/services/ReportingService';
import { tableauService } from '@/services/api';
import { computed, inject, ref, reactive, watch } from 'vue';
import { useRouter } from 'vue-router';
import { filters } from '@/utilities/Filters';
import { AppState } from '@/store';
import { Store } from 'vuex';
import ReportingUploadModal from '@/components/ReportingUploadModal.vue';
import DocumentEditModal from '@/components/v2/DocumentEditModal.vue';
import { Document, UpdateDocumentRequest, DocumentType } from '@/types/v2/Document';
import {
  Card,
  DocumentCard,
  isDocumentCard,
  isTableauDashboardCard,
  isPinned,
  TableauDashboardCard,
} from '@/types/analytics/Analytics';
import { documentService } from '@/services/v2/DocumentService';
import { IsUserAClient } from '@/utilities/AuthUtils.js';

// ------
// emits
// ------

const emit = defineEmits(['updated']);

// -----
// props
// -----

const props = defineProps<{
  loading: boolean;
  selectedCategoryName: string;
  dashboards: TableauDashboard[];
  documents: Document[];
  documentTypes: DocumentType[];
  tableauTypes: ReportingTableauType[];
  accountId?: number;
}>();

// -----
// data
// -----

const tableauDomain = import.meta.env.VITE_TABLEAU_BASE_DOMAIN;
const openReportingModal = ref<boolean>(false);
const target = reactive<Target>({
  card: null,
  action: null,
  type: null,
  modal: {
    title: '',
    open: false,
    actionLabel: '',
  },
});
const searchFilter = ref<string>('');
const selectedDashboard = ref<TableauDashboard | null>(null);
const store = inject<Store<AppState>>('store');

const router = useRouter();

// ----------
// functions
// ----------

function handleClearSearchFilter() {
  searchFilter.value = '';
}

function handleOpenUploadModal() {
  openReportingModal.value = true;
}

async function handleUploadDashboard() {
  emit('updated');
}

function openEditModal(card: Card) {
  if (isTableauDashboardCard(card) || isDocumentCard(card)) {
    target.card = card;
    target.action = 'edit';
    target.modal.open = true;
    target.type = isTableauDashboardCard(card) ? 'tableau' : 'document';
  }
}

async function handleTogglePin(card: Card) {
  if (isTableauDashboardCard(card)) {
    await tableauService.togglePinned(card.dashboard?.id);
  }
  if (isDocumentCard(card)) {
    await documentService.togglePinned(card.document?.documentId);
  }
  emit('updated');
}

function handlePublishDocument(card: DocumentCard) {
  target.card = card;
  target.type = 'document';
  target.action = card.document.published ? 'unpublish' : 'publish';
  target.modal.title = `Confirm ${card.document.published ? 'Unpublishing' : 'Publishing'} Document`;
  target.modal.actionLabel = card.document.published ? 'Unpublish' : 'Publish';
  target.modal.open = true;
}

function handlePublishDashboard(card: TableauDashboardCard) {
  target.card = card;
  target.type = 'tableau';
  target.action = card.dashboard.published ? 'unpublish' : 'publish';
  target.modal.title = `Confirm ${card.dashboard.published ? 'Unpublishing' : 'Publishing'} Tableau Dashboard`;
  target.modal.actionLabel = card.dashboard.published ? 'Unpublish' : 'Publish';
  target.modal.open = true;
}

function handleDelete(card: Card) {
  if (isTableauDashboardCard(card)) {
    target.card = card;
    target.type = 'tableau';
    target.action = 'delete';
    target.modal.title = 'Confirm Delete Dashboard';
    target.modal.actionLabel = 'Delete';
    target.modal.open = true;
  } else if (isDocumentCard(card)) {
    target.card = card;
    target.type = 'document';
    target.action = 'delete';
    target.modal.title = 'Confirm Delete Document';
    target.modal.actionLabel = 'Delete';
    target.modal.open = true;
  }
}

async function handleModalConfirm(request?: UpdateDocumentRequest) {
  if (target.card && isTableauDashboardCard(target.card)) {
    if (target.action === 'delete') {
      if (props.accountId) {
        await tableauService.deleteDashboard(target.card.dashboard.accountId, target.card.dashboard.id);
      } else {
        await tableauService.deleteDashboardV2(target.card.dashboard.id);
      }
      clearTarget();
      emit('updated');
    } else if (target.action === 'publish' || target.action === 'unpublish') {
      await tableauService.togglePublished(target.card.dashboard.id);
      clearTarget();
      emit('updated');
    }
  } else if (target.card && isDocumentCard(target.card)) {
    if (target.action === 'delete') {
      await documentService.deleteDocumentById(target.card.document.documentId);
      clearTarget();
      emit('updated');
    } else if (target.action === 'publish') {
      await documentService.publishDocument(target.card.document.documentId);
      clearTarget();
      emit('updated');
    } else if (target.action === 'unpublish') {
      await documentService.unPublishDocument(target.card.document.documentId);
      clearTarget();
      emit('updated');
    } else if (target.action === 'edit' && request) {
      await documentService.updateDocument(target.card.document.documentId, request);
      clearTarget();
      emit('updated');
    }
  }
}

function clearTarget() {
  target.card = null;
  target.type = null;
  target.action = null;
  target.modal.title = '';
  target.modal.actionLabel = '';
  target.modal.open = false;
}

async function handleModalCancel() {
  clearTarget();
}

async function handleSelect(card: Card) {
  if (isTableauDashboardCard(card)) {
    selectedDashboard.value = card.dashboard;
    if (props.accountId) {
      router.push(`/account/${card.dashboard.accountId}/reporting/category/${encodeURIComponent(props.selectedCategoryName)}/dashboard/${card.dashboard.id}`);
    } else {
      router.push(`/reporting/category/${encodeURIComponent(props.selectedCategoryName)}/dashboard/${card.dashboard.id}`);
    }
  } else if (isDocumentCard(card)) {
    const target = await documentService.getDocumentFileById(card.document.documentId);
    const anchorElement = document.createElement('a');
    anchorElement.href = target;
    anchorElement.download = card.document.displayName || 'download';
    anchorElement.setAttribute('target', '_blank');
    anchorElement.setAttribute('rel', 'noopener noreferrer');
    anchorElement.click();
    anchorElement.remove();
  }
}

function showTableauActions(card: Card) {
  return hasAnalyticsWritePermission.value && isTableauDashboardCard(card);
}

function showDocumentActions(card: Card) {
  return hasAnalyticsWritePermission.value && isDocumentCard(card);
}

function showPin(card: Card) {
  return hasAnalyticsWritePermission.value || isPinned(card);
}

function showDelete(card: Card) {
  return (tableauDomainExists.value && showTableauActions(card)) || showDocumentActions(card);
}

// ---------
// computed
// ---------

const isAClientAccount = computed(() => {
  return IsUserAClient(store?.state.user);
});

const isClientViewEnabled = computed(() => {
  return store?.state.clientViewState.isClientViewEnabled;
});

const hasAnalyticsWritePermission = computed<boolean>(() => {
  const roles = store?.state?.user?.roles || [];
  const permissions = store?.state?.user?.permissions || [];
  return roles.includes('admin') || permissions.includes('analytics:write');
});

const isUploadButtonVisible = computed<boolean>(() => {
  // Always show upload button for operational analytics if user has write permission
  return hasAnalyticsWritePermission.value;
});

const filteredDashboards = computed<TableauDashboard[]>(() => {
  return props.dashboards.filter((dashboard) => {
    return (
      dashboard.name.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
      dashboard.user.userEmail.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
      dashboard.dashboardUrl.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
      dashboard.accountDerivedName?.toLowerCase().includes(searchFilter.value.toLowerCase())
    );
  });
});

const filteredDocuments = computed<Document[]>(() => {
  return props.documents.filter((document) => {
    return (
      document.fileName.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
      document.displayName?.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
      document.documentTypeName?.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
      document.createdByUser.toLowerCase().includes(searchFilter.value.toLowerCase())
    );
  });
});

const analyticsCards = computed<Card[]>(() => {
  const cards: Card[] = [];

  // add all the pinned items first
  filteredDashboards.value.filter((dashboard) => dashboard.pinned).forEach((dashboard) => {
    cards.push({
      id: dashboard.id.toString(),
      type: 'tableau',
      url: dashboard.dashboardUrl,
      dashboard: dashboard,
      title: dashboard.name,
      client: '',
      created_by: dashboard.user.userEmail,
      updated_on: dashboard.updatedAt ? filters.date(dashboard.updatedAt) : '--',
    } as TableauDashboardCard);
  });

  filteredDocuments.value.filter((document) => document.pinned).forEach((document) => {
    cards.push({
      id: document.documentId,
      type: 'document',
      document: document,
      title: document.displayName || document.fileName,
      client: '',
      created_by: document.createdByUser,
      created_on: document.createdByDate,
      updated_on: document.updatedByDate ? filters.date(document.updatedByDate) : '--',
      pinned: document.pinned,
    } as DocumentCard);
  });

  // sort pinned cards
  cards.sort((a, b) => new Date(b.updated_on).getTime() - new Date(a.updated_on).getTime());

  const nonPinnedCards: Card[] = [];

  // then add all non-pinned items
  filteredDashboards.value.filter((dashboard) => !dashboard.pinned).forEach((dashboard) => {
    nonPinnedCards.push({
      id: dashboard.id.toString(),
      type: 'tableau',
      url: dashboard.dashboardUrl,
      dashboard: dashboard,
      title: dashboard.name,
      client: '',
      created_by: dashboard.user.userEmail,
      created_on: dashboard.createdDate,
      updated_on: dashboard.updatedAt ? filters.date(dashboard.updatedAt) : '--',
    } as TableauDashboardCard);
  });

  filteredDocuments.value.filter((document) => !document.pinned).forEach((document) => {
    nonPinnedCards.push({
      id: document.documentId,
      type: 'document',
      document: document,
      title: document.displayName || document.fileName,
      client: '',
      created_by: document.createdByUser,
      created_on: document.createdByDate || '',
      updated_on: document.updatedByDate ? filters.date(document.updatedByDate) : '--',
      pinned: document.pinned,
    } as DocumentCard);
  });

  nonPinnedCards.sort((a, b) => new Date(b.updated_on).getTime() - new Date(a.updated_on).getTime());
  cards.push(...nonPinnedCards);

  return cards;
});

const tableauDomainExists = computed<boolean>(() => !!tableauDomain);

const tableauTypes = computed<TableauType[]>(() => {
  return props.tableauTypes
    .filter((type) => type.tableauTypeName === props.selectedCategoryName)
    .map((type) => ({
      id: type.tableauTypeId,
      name: type.tableauTypeName,
    }));
});

const documentTypes = computed<DocumentType[]>(() => {
  /*
    These seem like placeholder values for the reporting hub. In the future
    there will likely be more types that do not match the selected category.
    When this happens, likely the ReportingDocumentType table would have to
    be expanded to have a category column.
   */
  return props.documentTypes
    .filter((type) => type.name === props.selectedCategoryName)
    .map((type) => ({
      id: type.id,
      name: type.name,
      domainId: type.domainId,
      domainName: type.domainName,
      analyticsTypeId: type.analyticsTypeId,
    } as DocumentType));
});


// ---------
// watchers
// ---------

watch(
  () => props.selectedCategoryName,
  async (newVal, oldVal) => {
    if (newVal !== oldVal) {
      searchFilter.value = '';
    }
  },
);
</script>

<template>
  <div class="tw:grow tw:bg-white">
    <main v-if="!$route.params.dashboardId" ref="resourceTypeHeader">
      <header
        class="tw:sticky tw:top-16 tw:mx-4 tw:grid tw:grid-cols-3 tw:items-center tw:border-b tw:border-gray-300 tw:bg-white tw:p-4">
        <h1 class="tw:text-2xl tw:font-bold tw:text-primary">{{ selectedCategoryName }}</h1>

        <label class="tw:input tw:input-md tw:mx-2 tw:flex tw:items-center tw:gap-2">
          <input type="text" placeholder="Search" class="tw:grow" v-model="searchFilter" />
          <XIcon v-if="searchFilter.length > 0" :size="15" class="tw:cursor-pointer" @click="handleClearSearchFilter" />
        </label>

        <div class="tw:flex tw:justify-self-end tw:pointer-events-none">
          <button
            v-if="isUploadButtonVisible"
            data-testid="analytics-upload-btn"
            class="tw:btn tw:btn-primary tw:btn-sm tw:mx-2 tw:my-2 tw:mr-2 tw:pointer-events-auto"
            :class="{ 'tw:btn-disabled': !tableauDomainExists }"
            @click="handleOpenUploadModal">
            Upload
          </button>
        </div>
      </header>

      <div v-if="analyticsCards.length === 0 && !loading">
        <div class="tw:mt-8 tw:flex tw:justify-center">
          <h2 class="tw:text-2xl tw:text-gray-500">No content found</h2>
        </div>
      </div>
      <div
        v-else
        class="tw:flex tw:min-h-[88svh] tw:w-full tw:flex-wrap tw:justify-start tw:overflow-auto tw:overflow-x-hidden tw:p-4">
        <div class="tw:flex tw:flex-col tw:flex-wrap tw:content-start tw:items-start tw:gap-4 tw:px-4 tw:xl:flex-row">
          <div
            v-for="card in analyticsCards"
            :key="card.id"
            class="tw:pointer-events-auto tw:flex tw:w-120 tw:cursor-pointer tw:flex-col tw:justify-between tw:gap-5 tw:rounded-md tw:border tw:border-solid tw:border-gray-300 tw:p-5 tw:shadow-md tw:transition-all tw:duration-150 tw:hover:border-gray-400 tw:hover:bg-gray-100 tw:hover:shadow-none"
            :class="{ 'tw:bg-gray-100 tw:border-gray-400': isPinned(card), 'tw:max-h-36 tw:min-h-36': isAClientAccount, 'tw:max-h-56 tw:min-h-56': !isAClientAccount }"
            @click="handleSelect(card)">

            <div class="tw:flex tw:flex-col tw:gap-2">
              <div class="tw:flex tw:items-center tw:gap-2">
                <ChartNoAxesCombinedIcon v-if="isTableauDashboardCard(card)" :size="18" />
                <FileIcon v-if="isDocumentCard(card)" :size="18" />
                <h2
                  class="tw:text-specialty-teal"
                  data-testid="card-title"
                  :class="{ 'tw:text-sm': card.title.length > 30 }"
                  v-html="filters.highlight(card.title, searchFilter)"></h2>
              </div>
              <div class="tw:flex tw:w-md tw:gap-1 tw:text-xs tw:font-medium tw:text-gray-600 tw:pt-1">
                <template v-if="isTableauDashboardCard(card)">
                  <div><ExternalLinkIcon :size="16" /></div>
                  <div
                    class="tw:overflow-hidden tw:text-ellipsis tw:text-nowrap"
                    v-html="filters.highlight(card.url.replace(/^https?:\/\//, ''), searchFilter)"></div>
                </template>
                <span
                  v-if="isDocumentCard(card)"
                  class="tw:badge tw:badge-accent"
                  v-html="filters.highlight(card.document.documentTypeName, searchFilter)"
                />
              </div>
            </div>

            <table class="tw:table-fixed" :class="{ 'tw:w-40' : isAClientAccount }">
              <tbody>
                <tr v-if="!isAClientAccount">
                  <td class="tw:font-bold">Created by:</td>
                  <td v-html="filters.highlight(card?.created_by ?? '', searchFilter)"></td>
                </tr>
                <tr>
                  <td class="tw:font-bold">Updated:</td>
                  <td>{{ card.updated_on }}</td>
                </tr>
              </tbody>
            </table>

            <div class="tw:flex tw:min-h-6 tw:justify-end tw:gap-1.5" v-if="!isClientViewEnabled">
              <button
                  v-if="showPin(card)"
                  class="tw:group tw:btn tw:btn-outline tw:btn-xs tw:w-8 tw:border tw:border-solid tw:border-gray-400 tw:hover:border-0 tw:hover:bg-primary"
                  :class="{ 'tw:border-0 tw:bg-primary': isPinned(card) }"
                  :disabled="!hasAnalyticsWritePermission"
                  @click.stop="() => handleTogglePin(card)">
                <PinIcon :size="16" class="tw:text-primary tw:group-hover:text-white"
                  :class="{ 'tw:text-white': isPinned(card) }"/>
              </button>

              <button
                v-if="showDocumentActions(card) && isDocumentCard(card)"
                class="tw:group tw:btn tw:btn-outline tw:btn-xs tw:w-8 tw:border tw:border-solid tw:border-gray-400 tw:hover:border-0 tw:hover:bg-primary"
                :class="{ 'tw:border-green-500': card.document.published }"
                @click.stop="() => handlePublishDocument(card)">
                <UsersIcon
                  :size="16"
                  class="tw:text-primary tw:group-hover:text-white"
                  :class="{ 'tw:stroke-green-500': card.document.published }" />
              </button>

              <button
                  v-if="showTableauActions(card) && isTableauDashboardCard(card)"
                  class="tw:group tw:btn tw:btn-outline tw:btn-xs tw:w-8 tw:border tw:border-solid tw:border-gray-400 tw:hover:border-0 tw:hover:bg-primary"
                  :class="{ 'tw:border-green-500': card.dashboard.published }"
                  @click.stop="() => handlePublishDashboard(card)">
                <UsersIcon
                    :size="16"
                    class="tw:text-primary tw:group-hover:text-white"
                    :class="{ 'tw:stroke-green-500': card.dashboard.published }" />
              </button>

              <button
                v-if="((tableauDomainExists && showTableauActions(card)) || showDocumentActions(card)) && !isAClientAccount"
                class="tw:group tw:btn tw:btn-outline tw:btn-xs tw:w-8 tw:border tw:border-solid tw:border-gray-400 tw:hover:border-0 tw:hover:bg-primary"
                @click.stop="() => openEditModal(card)">
                <EditIcon :size="16" class="tw:text-primary tw:group-hover:text-white" />
              </button>

              <button
                v-if="showDelete(card)"
                class="tw:group tw:btn tw:btn-outline tw:btn-xs tw:w-8 tw:border tw:border-solid tw:border-gray-400 tw:hover:border-0 tw:hover:bg-agency-red"
                @click.stop="() => handleDelete(card)">
                <Trash2Icon class="tw:text-primary tw:group-hover:text-white" :size="16" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>

    <router-view v-else />

    <AppModal
      :show="target.modal.open && target.action !== 'edit'"
      :handle-cancel="() => handleModalCancel()"
      :handle-action="() => handleModalConfirm()"
      :action-button-label="target.modal.actionLabel">
      <template #title>
        <h2 class="tw:pb-3">{{ target.modal.title }}</h2>
      </template>
    </AppModal>

    <DocumentEditModal
      :show="target.modal.open && target.action === 'edit' && target.type === 'document'"
      title="Edit Document"
      ref="editDocumentModal"
      :document="(target.card && isDocumentCard(target.card) && target.card.document) || undefined"
      :policy-document-types="documentTypes"
      :include-tags="false"
      @close="clearTarget()"
      @update="(request) => handleModalConfirm(request)" />

    <TableauModal
      :show="target.modal.open && target.action === 'edit' && target.type === 'tableau'"
      mode="edit"
      :dashboard="(target.card && isTableauDashboardCard(target.card) && target.card.dashboard) || undefined"
      :tableauDomain="tableauDomain"
      :tableau-types="tableauTypes"
      :enableAccountSelect="false"
      @close="clearTarget()"
      @save="handleUploadDashboard" />

    <ReportingUploadModal
      v-model:open="openReportingModal"
      :selected-category-name="selectedCategoryName"
      :account-id="props.accountId"
      @submit="handleUploadDashboard" />
  </div>
</template>
